import { z } from "zod";

import {
  basicPasswordSchema,
  emailSchema,
  nameSchema,
  strongPasswordSchema,
  superStrongPasswordSchema,
} from "./common";

// Login schema
export const loginSchema = z.object({
  email: emailSchema,
  password: basicPasswordSchema,
});

// Registration schema
export const registerSchema = z.object({
  email: emailSchema,
  firstName: nameSchema,
  lastName: nameSchema,
  organizationName: z
    .string({ message: "Organization name is required" })
    .refine(
      (val) => val.trim().length >= 2,
      "Organization name must be at least 2 characters",
    ),
  password: strongPasswordSchema,
  phone: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val || /^[+]?[1-9][\d]{0,15}$/.test(val.replace(/[\s\-().]/g, "")),
      "Please enter a valid phone number",
    ),
});

// Forgot password schema
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

// Reset password schema
export const resetPasswordSchema = z.object({
  password: superStrongPasswordSchema,
  token: z.string(),
});

// Accept invite schema - this one requires validation logic so we keep it separate
export const acceptInviteSchema = z
  .object({
    confirmPassword: z.string(),
    email: emailSchema,
    password: strongPasswordSchema,
    phone: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val || /^[+]?[1-9][\d]{0,15}$/.test(val.replace(/[\s\-().]/g, "")),
        "Please enter a valid phone number",
      ),
    token: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// Export types
export type LoginData = z.infer<typeof loginSchema>;
export type RegisterData = z.infer<typeof registerSchema>;
export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;
export type AcceptInviteData = z.infer<typeof acceptInviteSchema>;
